import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { BRAND, CHANGELOG } from "@/lib/brand";
import { Reveal, SectionLabel } from "@/components/Motion";
import { Footer } from "@/components/Footer";

const TYPE_STYLE = {
  Added: "border-[hsl(var(--orv-blue))]/50 text-[hsl(var(--orv-blue))]",
  Improved: "border-white/25 text-white/70",
  Fixed: "border-emerald-400/45 text-emerald-300/80",
};

export default function Changelog() {
  return (
    <main className="relative min-h-screen bg-[#08080a]">
      <header className="glass fixed inset-x-0 top-0 z-50 border-b border-white/8">
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="changelog-home-link" className="group flex items-center gap-3.5">
            <img src={BRAND.mark} alt="Orvyn" className="h-8 w-auto" />
            <span className="hidden h-8 w-px bg-white/12 sm:block" />
            <span className="hidden sm:block">
              <span className="block font-display text-[15px] font-bold uppercase tracking-[0.3em] leading-none text-white">
                Orvyn
              </span>
              <span className="mt-1.5 block font-mono text-[9px] uppercase tracking-[0.34em] text-white/35">
                FiveM Scripts
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="group flex items-center gap-2 border border-white/12 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white/65 transition-colors duration-300 hover:border-white/40 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
              Back
            </Link>
            <Link
              to="/#store"
              data-testid="changelog-store-button"
              className="group relative hidden overflow-hidden bg-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-black sm:block"
            >
              <span className="relative z-10">Shop</span>
              <span className="absolute inset-0 z-0 -translate-y-full bg-[hsl(var(--orv-blue))] transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
            </Link>
          </div>
        </div>
      </header>

      <section className="hairline-grid border-b border-white/8 px-5 pb-16 pt-36 sm:px-8 lg:pb-24 lg:pt-44">
        <div className="mx-auto max-w-[1400px]">
          <SectionLabel index="§ 00">Release history</SectionLabel>
          <h1 className="mt-6 max-w-3xl font-display text-[clamp(2.2rem,5.5vw,4.4rem)] font-extrabold uppercase leading-[0.95] tracking-[-0.02em] text-white">
            Every version, on the record.
          </h1>
          <p className="mt-7 max-w-xl text-sm leading-relaxed text-white/45">
            Fight Studio is actively developed. Updates land in your Tebex account automatically, and
            every release is posted here and announced in the Discord.
          </p>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-8 lg:py-28">
        <div className="mx-auto max-w-[1400px]">
          {CHANGELOG.map((release, ri) => (
            <Reveal key={release.version} delay={ri * 0.06}>
              <article
                data-testid={`changelog-release-${release.version.replace(/\s+/g, "-")}`}
                className="grid gap-8 border-t border-white/8 py-12 lg:grid-cols-12 lg:gap-12 lg:py-16"
              >
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-3">
                    <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                      {release.version}
                    </h2>
                    {release.current && (
                      <span className="border border-[hsl(var(--orv-blue))]/50 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--orv-blue))]">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
                    {release.date}
                  </div>
                  <p className="mt-6 max-w-sm text-[13px] leading-relaxed text-white/45">
                    {release.summary}
                  </p>
                </div>

                <div className="lg:col-span-8">
                  <ul className="divide-y divide-white/6">
                    {release.entries.map((e, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55, delay: i * 0.035, ease: [0.16, 1, 0.3, 1] }}
                        className="flex items-start gap-4 py-4"
                      >
                        <span
                          className={`mt-0.5 shrink-0 border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] ${
                            TYPE_STYLE[e.type] || TYPE_STYLE.Improved
                          }`}
                        >
                          {e.type}
                        </span>
                        <span className="text-sm leading-relaxed text-white/60">{e.text}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
          <div className="h-px w-full bg-white/8" />

          <Reveal delay={0.1}>
            <div className="mt-16 border border-white/10 p-8 sm:p-10">
              <div className="label">What's next</div>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/50">
                Earlier release notes are being migrated across, and every future version will be
                logged here the day it ships. Want to know what's in the next build? Ask in the
                Discord — feature requests from buyers go straight onto the roadmap.
              </p>
              <a
                href={BRAND.discord}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="changelog-discord-link"
                className="mt-7 inline-block border border-white/18 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/75 transition-colors duration-300 hover:border-white/50 hover:text-white"
              >
                Join the Discord
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
